import React, { useState, useEffect } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import InvoiceListLayer from "../components/InvoiceListLayer";
import { API_URL, LOCATION_ID } from "..";
import { Spinner } from "react-bootstrap";
import QuoteListLayer from "../components/QuoteListLayer";
import { Icon } from "@iconify/react";
import Select from "react-select";

const staffSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    height: "44px",
    borderRadius: "12px",
    borderColor: state.isFocused ? "#111827" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(17, 24, 39, 0.08)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#111827" : "#d1d5db",
    },
    fontSize: "14px",
    backgroundColor: "#ffffff",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({
    ...base,
    height: "44px",
    padding: "0 12px",
  }),
  input: (base) => ({
    ...base,
    margin: "0px",
    color: "#111827",
    fontSize: "14px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: "44px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "14px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827",
    fontSize: "14px",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
    zIndex: 9999,
    overflow: "hidden",
  }),
  menuList: (base) => ({
    ...base,
    padding: "6px",
    maxHeight: "220px",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: "8px",
    fontSize: "13px",
    padding: "8px 12px",
    backgroundColor: state.isSelected
      ? "#111827"
      : state.isFocused
      ? "#f3f4f6"
      : "transparent",
    color: state.isSelected ? "#ffffff" : "#1f2937",
    cursor: "pointer",
    "&:active": {
      backgroundColor: "#e5e7eb",
    },
  }),
};

const QuoteListPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalInvoicesCount: 0,
    totalInvoiceAmountCount: 0,
    paidInvoiceCount: 0,
    paidInvoiceAmountCount: 0,
    overdueInvoiceCount: 0,
    overdueInvoiceAmountCount: 0,
  });

  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(formatDate(firstDay));
  const [toDate, setToDate] = useState(formatDate(today));
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const isAdmin = currentUser?.roles?.role === "admin" || currentUser?.role === "admin";

  const filteredStaffList = staffList.filter((staff) => {
    if (!selectedCity) return true;
    const staffCityId = staff.cityId ?? staff.city?.id;
    return (
      staffCityId !== undefined &&
      staffCityId !== null &&
      String(staffCityId) === String(selectedCity)
    );
  });

  const staffOptions = [
    { value: "", label: "All Staff" },
    ...filteredStaffList.map((staff) => ({
      value: staff.crm_id || String(staff.id),
      label: staff.name || staff.email,
    })),
  ];

  const cityOptions = [
    { value: "", label: "All Cities" },
    ...cities.map((city) => ({
      value: String(city.id),
      label: city.name,
    })),
  ];

  const fetchCities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/city`);
      const resData = await res.json();
      setCities(Array.isArray(resData) ? resData : []);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await fetch(`${API_URL}/api/user/fetch`);
      const resData = await res.json();
      if (resData && Array.isArray(resData.data)) {
        const sorted = [...resData.data].sort((a, b) =>
          (a.name || a.email || "").localeCompare(b.name || b.email || "")
        );
        setStaffList(sorted);
      }
    } catch (err) {
      console.error("Error fetching staff:", err);
    }
  };

  const fetchInvoices = async (from, to, city = selectedCity, staff = selectedStaff) => {
    setLoading(true);
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      window.location.href = "/sign-in";
      return;
    }

    let headersList = {
      Accept: "*/*",
      "Content-Type": "application/json",
    };
    const userIsAdmin = user?.roles?.role === "admin" || user?.role === "admin";
    const cityQuery = userIsAdmin && city ? `&cityId=${city}` : "";
    const staffQuery = userIsAdmin && staff ? `&staffId=${staff}` : "";

    let url;
    if (userIsAdmin) {
      url = `${API_URL}/api/invoice/get/quote?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}${cityQuery}${staffQuery}`;
    } else {
      url = `${API_URL}/api/invoice/get/user/quote/${user?.crm_id}?fromDate=${from}&toDate=${to}&locationId=${LOCATION_ID}`;
    }

    try {
      let response = await fetch(url, {
        method: "GET",
        headers: headersList,
      });
      const data = await response.json();
      setData(data.invoices || data || []);
    } catch (error) {
      console.error("Error fetching quotes:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(fromDate, toDate, selectedCity, selectedStaff);
    fetchCities();
    if (isAdmin) {
      fetchStaff();
    }
  }, []);

  const handleFilter = () => {
    fetchInvoices(fromDate, toDate, selectedCity, selectedStaff);
  };

  return (
    <>
      {/* MasterLayout */}
      <MasterLayout>
        {/* Breadcrumb */}

        <Breadcrumb title="Quotes" />

        <div className="quote-page-responsive">
          <style>
            {`
              .quote-page-responsive {
                --quote-primary: #111827;
                --quote-primary-hover: #1f2937;
                --quote-border: #e5e7eb;
                --quote-muted: #6b7280;
                --quote-soft: #f9fafb;
                --quote-card: #ffffff;
              }

              .quote-filter-card {
                background: var(--quote-card);
                border: 1px solid var(--quote-border);
                border-radius: 18px;
                padding: 18px;
                margin-bottom: 18px;
                box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
              }

              .quote-filter-grid {
                display: grid;
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
                align-items: end;
                gap: 14px;
              }

              .quote-filter-grid.has-staff {
                grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
              }

              .quote-filter-title {
                display: flex;
                align-items: center;
                gap: 10px;
                margin-bottom: 14px;
                color: var(--quote-primary);
                font-size: 15px;
                font-weight: 850;
              }

              .quote-filter-title-icon {
                width: 34px;
                height: 34px;
                border-radius: 11px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: var(--quote-primary);
                background: #f3f4f6;
                border: 1px solid var(--quote-border);
                flex: 0 0 auto;
              }

              .quote-field-label {
                color: #374151;
                font-size: 12px;
                font-weight: 850;
                margin-bottom: 7px;
                letter-spacing: 0.02em;
              }

              .quote-filter-card .form-control {
                min-height: 44px;
                border-radius: 12px;
                border: 1px solid var(--quote-border);
                color: var(--quote-primary);
                box-shadow: none;
                font-size: 14px;
              }

              .quote-filter-card .form-control:focus {
                border-color: var(--quote-primary);
                box-shadow: 0 0 0 4px rgba(17, 24, 39, 0.08);
              }

              .quote-filter-button {
                min-height: 44px;
                border: 1px solid var(--quote-primary);
                background: var(--quote-primary);
                color: #ffffff;
                border-radius: 12px;
                padding: 10px 20px;
                font-size: 14px;
                font-weight: 850;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                transition: all 0.15s ease;
                white-space: nowrap;
              }

              .quote-filter-button:hover {
                background: var(--quote-primary-hover);
                border-color: var(--quote-primary-hover);
                transform: translateY(-1px);
              }

              .quote-loading-card,
              .quote-empty-card {
                border: 1px solid var(--quote-border);
                border-radius: 18px;
                background: #fff;
                padding: 28px 18px;
                text-align: center;
                box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
              }

              .quote-empty-icon {
                width: 56px;
                height: 56px;
                border-radius: 18px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                background: #f3f4f6;
                color: var(--quote-muted);
                margin-bottom: 12px;
                font-size: 28px;
              }

              @media (max-width: 767.98px) {
                .quote-filter-card {
                  border-radius: 14px;
                  padding: 14px;
                  margin-bottom: 14px;
                }

                .quote-filter-grid,
                .quote-filter-grid.has-staff {
                  grid-template-columns: 1fr;
                  gap: 12px;
                }

                .quote-filter-button {
                  width: 100%;
                }
              }
            `}
          </style>

          <div className="quote-filter-card">
            <div className="quote-filter-title">
              <span className="quote-filter-title-icon">
                <Icon icon="mdi:calendar-filter-outline" />
              </span>
              <span>Filter quotes by date{isAdmin ? ", city & staff" : " & city"}</span>
            </div>

            <div className={`quote-filter-grid ${isAdmin ? "has-staff" : ""}`}>
              <div>
                <label className="quote-field-label">From</label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div>
                <label className="quote-field-label">To</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              {isAdmin && (
                <div>
                  <label className="quote-field-label">City</label>
                  <Select
                    isSearchable
                    options={cityOptions}
                    value={
                      cityOptions.find(
                        (opt) => String(opt.value) === String(selectedCity)
                      ) || cityOptions[0]
                    }
                    onChange={(option) => {
                      const newCity = option ? option.value : "";
                      setSelectedCity(newCity);

                      let nextStaff = selectedStaff;
                      if (newCity && selectedStaff) {
                        const currentStaffObj = staffList.find(
                          (s) =>
                            (s.crm_id && String(s.crm_id) === String(selectedStaff)) ||
                            String(s.id) === String(selectedStaff)
                        );
                        const staffCityId = currentStaffObj?.cityId ?? currentStaffObj?.city?.id;
                        if (!currentStaffObj || String(staffCityId) !== String(newCity)) {
                          nextStaff = "";
                          setSelectedStaff("");
                        }
                      }

                      fetchInvoices(fromDate, toDate, newCity, nextStaff);
                    }}
                    placeholder="Search City..."
                    styles={staffSelectStyles}
                  />
                </div>
              )}

              {isAdmin && (
                <div>
                  <label className="quote-field-label">Staff</label>
                  <Select
                    isSearchable
                    options={staffOptions}
                    value={
                      staffOptions.find(
                        (opt) => String(opt.value) === String(selectedStaff)
                      ) || staffOptions[0]
                    }
                    onChange={(option) => {
                      const newStaff = option ? option.value : "";
                      setSelectedStaff(newStaff);
                      fetchInvoices(fromDate, toDate, selectedCity, newStaff);
                    }}
                    placeholder="Search Staff..."
                    styles={staffSelectStyles}
                  />
                </div>
              )}

              <div>
                <button
                  className="quote-filter-button"
                  type="button"
                  onClick={handleFilter}
                  disabled={loading}
                >
                  <Icon icon="mdi:tune-variant" />
                  {loading ? "Filtering..." : "Filter"}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="quote-loading-card">
              <Spinner />
              <div className="mt-3 text-muted fw-medium">Loading quotes...</div>
            </div>
          ) : null}
          
          {data.length === 0 && loading === false ? (
            <div className="quote-empty-card">
              <div className="quote-empty-icon">
                <Icon icon="mdi:file-document-alert-outline" />
              </div>
              <h6 className="mb-1 fw-bold">No quotes found</h6>
              <p className="mb-0 text-muted">Try changing the date range or create a new quote.</p>
            </div>
          ) : null}

          {data.length > 0 && <QuoteListLayer jobsData={data} />}
        </div>
      </MasterLayout>
    </>
  );
};

export default QuoteListPage;
